<template>
  <div class="full-text-review-action-controls">
    <div class="labels-and-back-link mr-4">
      <div class="labels">
        <last-voters-cluster-prefix
          v-if="showLastVotersPrefix"
          :voters="lastVoters"
        />
        {{ label }}
      </div>
      <back-link
        v-if="showBackLink"
        current-stage="Data Extraction"
        @click="$emit('back')"
      />
      <change-previous-stage-link
        v-if="showBackToPreviousStageLink"
        previous-stage="Screening"
        @click="$emit('change-previous-stage')"
      />
    </div>
    <div class="actions">
      <vote-controls
        v-if="showVoteButtons"
        hide-not-sure
        :disabled="disabled"
        @vote="onVoteButtonClick"
      />
      <assessment-dropdown
        v-if="showVoteDropdown"
        :value="dropdownValue"
        inclusion-assessment
        :show-revoke-option="allowRevoke"
        :disabled="disabled"
        @input="onVoteDropdownChange"
        @revoke="onRevokeVote"
      />
    </div>
  </div>
</template>

<script>
import LastVotersClusterPrefix from './LastVotersClusterPrefix';
import BackLink from './BackLink';
import ChangePreviousStageLink from './ChangePreviousStageLink';
import AssessmentDropdown from '@/components/References/AssessmentDropdown';
import VoteControls from '@/components/ScreenReferencesPage/VoteControls';
import {
  Reference,
  ReviewMember,
  FullTextReviewVote,
  FullTextReviewOutcome,
  Review,
} from '@/models';
import {
  ReferenceActionState,
  InclusionAssessment,
  ScreeningOutcomeResolution,
} from '@/enums';

const ACTION_STATE_TO_PROPS = {
  [ReferenceActionState.PENDING_ACTION]: {
    label: 'How would you like to review this reference?',
    showVoteButtons: true,
    showBackToPreviousStageLink: true,
  },
  [ReferenceActionState.PENDING_ACTION_BY_YOU]: {
    prefixWithLastVoter: true,
    label:
      'has reviewed this reference. How would you like to review this reference?',
    showVoteButtons: true,
  },
  [ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN]: {
    label:
      'You have already reviewed this reference. You may change it, otherwise no further action is required.',
    showVoteDropdown: true,
    dropdownValue: (reference, researcher) =>
      reference.fullTextReviewVotes.find(
        (review) => review.reviewer?.researcher?.id === researcher.id
      ).vote,
  },
  [ReferenceActionState.PENDING_ACTION_BY_OTHERS]: {
    label:
      'Requires a review from users with elevated permissions. No further action required from you.',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU]: {
    prefixWithLastVoters: true,
    label:
      'have conflicting reviews. How would you like to resolve this?',
    showVoteButtons: true,
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS]: {
    prefixWithLastVoters: true,
    label:
      'have conflicting reviews. No further action required from you.',
  },
  [ReferenceActionState.EXCLUDED_BY_CONSENSUS]: {
    prefixWithLastVoters: true,
    label:
      'excluded this from the review after reaching consensus during Full Text Review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.EXCLUDE,
  },
  [ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION]: {
    prefixWithLastVoter: true,
    label:
      'excluded this from the review after resolving a conflict during Full Text Review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.EXCLUDE,
  },
  [ReferenceActionState.EXCLUDED_BY_OVERRIDE]: {
    prefixWithLastVoter: true,
    label:
      'overrode the previous outcome and excluded this from the review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.EXCLUDE,
  },
  [ReferenceActionState.EXCLUDED_ON_UPLOAD]: {
    prefixWithLastVoter: true,
    label: 'excluded this from the review during the upload process.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.EXCLUDE,
  },
  [ReferenceActionState.INCLUDED_BY_CONSENSUS]: {
    prefixWithLastVoters: true,
    label:
      'included this in the review after reaching consensus during Full Text Review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.INCLUDE,
    showBackLink: true,
  },
  [ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION]: {
    prefixWithLastVoter: true,
    label:
      'included this in the review after resolving a conflict during Full Text Review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.INCLUDE,
    showBackLink: true,
  },
  [ReferenceActionState.INCLUDED_BY_OVERRIDE]: {
    prefixWithLastVoter: true,
    label:
      'overrode the previous outcome and included this in the review.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.INCLUDE,
    showBackLink: true,
  },
  [ReferenceActionState.INCLUDED_ON_UPLOAD]: {
    prefixWithLastVoter: true,
    label: 'included this in the review during the upload process.',
    showVoteDropdown: true,
    dropdownValue: () => InclusionAssessment.INCLUDE,
    showBackLink: true,
  },
};

const VOTE_CONTROLS_TO_INCLUSION_ASSESSMENT = {
  YES: InclusionAssessment.INCLUDE,
  NO: InclusionAssessment.EXCLUDE,
};

export default {
  name: 'FullTextReviewActionControls',
  components: {
    LastVotersClusterPrefix,
    BackLink,
    ChangePreviousStageLink,
    AssessmentDropdown,
    VoteControls,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentReviewMember: {
      type: ReviewMember,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'back',
    'change-previous-stage',
    'create-full-text-review-vote',
    'change-full-text-review-vote',
    'revoke-full-text-review-vote',
    'resolve-full-text-review-conflict',
    'change-full-text-review-outcome',
  ],
  computed: {
    currentResearcher() {
      return this.currentReviewMember.researcher;
    },
    prefixWithLastVoter() {
      return this.actionProps.prefixWithLastVoter;
    },
    prefixWithLastVoters() {
      return this.actionProps.prefixWithLastVoters;
    },
    showLastVotersPrefix() {
      return this.prefixWithLastVoter || this.prefixWithLastVoters;
    },
    lastVoters() {
      if (this.prefixWithLastVoter) {
        return [this.lastVoter];
      }
      return this.reference.fullTextReviewVotes
        .slice(0, 2)
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean);
    },
    lastVoter() {
      const useVotesToFindLast = [
        ReferenceActionState.PENDING_ACTION_BY_YOU,
      ].includes(this.actionState);
      return useVotesToFindLast
        ? this.reference.fullTextReviewVotes[0]?.reviewer?.researcher
        : this.reference.fullTextReviewOutcome?.resolvedBy
            ?.researcher;
    },
    label() {
      return this.actionProps.label;
    },
    showVoteButtons() {
      return this.actionProps.showVoteButtons;
    },
    showVoteDropdown() {
      return this.actionProps.showVoteDropdown;
    },
    dropdownValue() {
      return this.actionProps.dropdownValue?.(
        this.reference,
        this.currentResearcher
      );
    },
    showBackLink() {
      return this.actionProps.showBackLink;
    },
    showBackToPreviousStageLink() {
      return this.actionProps.showBackToPreviousStageLink;
    },
    actionProps() {
      return ACTION_STATE_TO_PROPS[this.actionState];
    },
    actionState() {
      if (this.reference.isPendingFullTextReview()) {
        if (this.review.fullTextReviewVotesRequired === 1) {
          return this.isTrustedVoter
            ? ReferenceActionState.PENDING_ACTION
            : ReferenceActionState.PENDING_ACTION_BY_OTHERS;
        }
        return ReferenceActionState.PENDING_ACTION;
      }
      if (this.reference.isPartiallyReviewed()) {
        const hasVoteByYou = this.reference.hasFullTextReviewVoteFrom(
          this.currentResearcher
        );
        const trustedVoters = this.review
          .getTrustedFullTextReviewers()
          .map((member) => member.researcher);
        const hasTrustedVote = this.reference.hasFullTextReviewVoteFromAny(
          trustedVoters
        );
        if (hasVoteByYou) {
          return ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
        } else if (this.isTrustedVoter || hasTrustedVote) {
          return ReferenceActionState.PENDING_ACTION_BY_YOU;
        } else {
          return ReferenceActionState.PENDING_ACTION_BY_OTHERS;
        }
      }
      if (this.reference.hasFullTextReviewConflicts()) {
        const isConflictResolver = this.review.isFullTextReviewConflictResolver(
          this.currentReviewMember
        );
        return isConflictResolver
          ? ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU
          : ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS;
      }
      if (this.reference.isReviewed()) {
        const outcome = this.reference.fullTextReviewOutcome;
        const included = outcome.isIncluded();
        if (outcome.isConsensus()) {
          return included
            ? ReferenceActionState.INCLUDED_BY_CONSENSUS
            : ReferenceActionState.EXCLUDED_BY_CONSENSUS;
        }
        if (outcome.isConflictResolution()) {
          return included
            ? ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION
            : ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION;
        }
        if (outcome.isManualUpdate()) {
          return included
            ? ReferenceActionState.INCLUDED_BY_OVERRIDE
            : ReferenceActionState.EXCLUDED_BY_OVERRIDE;
        }
        if (outcome.isPreScreened()) {
          return included
            ? ReferenceActionState.INCLUDED_ON_UPLOAD
            : ReferenceActionState.EXCLUDED_ON_UPLOAD;
        }
        throw new Error(
          `Unable to map the full text review outcome to a review action ${outcome}`
        );
      }
      throw new Error(
        `Unable to map the review state to a review action ${this.reference.fullTextReviewState}`
      );
    },
    isTrustedVoter() {
      return this.review.researcherIsTrustedFullTextReviewer(
        this.currentResearcher
      );
    },
    allowRevoke() {
      return (
        this.actionState ===
        ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN
      );
    },
  },
  methods: {
    onVoteButtonClick(vote) {
      vote = VOTE_CONTROLS_TO_INCLUSION_ASSESSMENT[vote];
      this.processAssessment(vote);
    },
    onVoteDropdownChange(outcome) {
      this.processAssessment(outcome);
    },
    processAssessment(assessment) {
      switch (this.actionState) {
        case ReferenceActionState.PENDING_ACTION:
        case ReferenceActionState.PENDING_ACTION_BY_YOU:
          this.onCreateFullTextReviewVote(assessment);
          break;
        case ReferenceActionState.PENDING_ACTION_BY_OTHERS:
          this.onChangeFullTextReviewVote(assessment);
          break;
        case ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU:
          this.onResolveConflict(assessment);
          break;
        default:
          this.onChangeFullTextReviewOutcome(assessment);
          break;
      }
    },
    onCreateFullTextReviewVote(vote) {
      const voteToCreate = new FullTextReviewVote({
        vote,
        reference: this.reference,
        reviewer: this.currentReviewMember,
      });
      this.$emit('create-full-text-review-vote', voteToCreate);
    },
    onChangeFullTextReviewVote(vote) {
      const existingVote = this.reference.getFullTextReviewVoteBy(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          `Unable to find existing vote for ${this.currentResearcher}`
        );
      }
      const voteToUpdate = new FullTextReviewVote({
        id: existingVote.id,
        vote,
        reference: this.reference,
        reviewer: this.currentReviewMember,
      });
      this.$emit('change-full-text-review-vote', voteToUpdate);
    },
    onRevokeVote() {
      const existingVote = this.reference.getFullTextReviewVoteBy(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          `Unable to find existing vote for ${this.currentResearcher}`
        );
      }
      this.$emit('revoke-full-text-review-vote', existingVote);
    },
    onResolveConflict(outcome) {
      const outcomeToCreate = new FullTextReviewOutcome({
        reference: this.reference,
        resolution: ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
        outcome,
      });
      this.$emit(
        'resolve-full-text-review-conflict',
        outcomeToCreate
      );
    },
    onChangeFullTextReviewOutcome(outcome) {
      const existingOutcome = this.reference.fullTextReviewOutcome;
      if (!existingOutcome) {
        throw new Error(
          `Unable to find existing outcome for ${this.reference}`
        );
      }
      const outcomeToUpdate = new FullTextReviewOutcome({
        id: existingOutcome.id,
        reference: this.reference,
        resolution: ScreeningOutcomeResolution.UPDATED_OUTCOME,
        outcome,
      });
      this.$emit('change-full-text-review-outcome', outcomeToUpdate);
    },
  },
};
</script>

<style lang="scss" scoped>
.full-text-review-action-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-size: 14px;
  font-weight: 300;

  .inline-block {
    display: inline-block;
  }
}
</style>
